/**
 * Environment configuration with validation
 * All environment variables should be prefixed with VITE_ to be exposed to the client
 */

interface EnvConfig {
  NODE_ENV: 'development' | 'production' | 'test';
  API_BASE_URL: string;
  API_TIMEOUT: number;
  ENABLE_ANALYTICS: boolean;
  CLERK_PUBLISHABLE_KEY: string;
}

/**
 * Get and validate environment variables
 */
function getEnvVar(key: string, defaultValue?: string): string {
  const value = import.meta.env[key] || defaultValue;
  if (value === undefined) {
    throw new Error(`Missing required environment variable: ${key}`);
  }
  return value;
}

/**
 * Parse boolean from environment variable
 */
function parseBoolean(
  value: string | undefined,
  defaultValue = false
): boolean {
  if (!value) return defaultValue;
  return value.toLowerCase() === 'true' || value === '1';
}

/**
 * Validated environment configuration
 */
export const env: EnvConfig = {
  NODE_ENV: (import.meta.env.MODE as EnvConfig['NODE_ENV']) || 'development',
  API_BASE_URL: getEnvVar('VITE_API_BASE_URL', 'http://localhost:8080/api'),
  API_TIMEOUT: Number(getEnvVar('VITE_API_TIMEOUT', '30000')),
  ENABLE_ANALYTICS: parseBoolean(import.meta.env.VITE_ENABLE_ANALYTICS),
  CLERK_PUBLISHABLE_KEY: getEnvVar(
    'VITE_CLERK_PUBLISHABLE_KEY',
    'pk_test_placeholder'
  ),
};

/**
 * Check if running in development mode
 */
export const isDevelopment = env.NODE_ENV === 'development';

/**
 * Check if running in production mode
 */
export const isProduction = env.NODE_ENV === 'production';

/**
 * Check if running in test mode
 */
export const isTest = env.NODE_ENV === 'test';
